// generateTimelapseVideo — the T3-facing 一発 API (T2 slice;
// growth-timelapse-share-2026-07-02.md §5.2-§5.3): options in → progress
// callbacks → `Promise` of the mp4 `Blob` (or a typed failure outcome).
//
// This is the main-thread half of the worker pair: it spawns one
// `timelapseWorker` per call, posts the request, relays `progress`
// messages, and resolves on `result`. The worker is ALWAYS terminated when
// the promise settles — normal completion, failure, worker crash, or
// cancel — so callers never leak a realm holding a 64 MB-class replay
// engine.
//
// Cancel: pass an `AbortSignal`. Abort tears the worker down immediately
// (`terminate()` — no cooperative flag, see `encodeJob.ts` header) and the
// promise resolves `{ outcome: 'cancelled' }`. The promise never rejects;
// every failure is a member of the outcome union (repo convention:
// outcome-union-for-data, throw-for-bugs).
//
// The `?worker` import lives in `worker/timelapseWorkerFactory.ts` — T3
// passes `createTimelapseWorker` as `workerFactory`; tests pass a fake.

import type { TimelapseEncodeOutcome, TimelapseEncodeRequest } from './encodeJob.js';
import type { TimelapseProgress } from './progress.js';
import type {
  TimelapseWorkerInbound,
  TimelapseWorkerOutbound,
} from './worker/timelapseWorkerProtocol.js';

export type GenerateTimelapseVideoOutcome =
  | TimelapseEncodeOutcome
  /** Aborted via `signal` before the result landed. */
  | { readonly outcome: 'cancelled' }
  /** The worker crashed (script load / uncaught error) before producing a result. */
  | { readonly outcome: 'worker-error'; readonly message: string };

export interface GenerateTimelapseVideoOptions extends TimelapseEncodeRequest {
  /**
   * Worker constructor — pass `createTimelapseWorker` from
   * `worker/timelapseWorkerFactory.js` in app code. Required (not
   * defaulted) so this module never touches a `?worker` import and stays
   * node-testable; see factory module header.
   */
  readonly workerFactory: () => Worker;
  /** Frame-level progress (phase / framesDone / frameCount / overall ratio). */
  readonly onProgress?: (p: TimelapseProgress) => void;
  /** Cancel hook — abort terminates the worker outright. */
  readonly signal?: AbortSignal;
}

export const generateTimelapseVideo = (
  options: GenerateTimelapseVideoOptions,
): Promise<GenerateTimelapseVideoOutcome> => {
  const { workerFactory, onProgress, signal, ...request } = options;
  if (signal?.aborted === true) {
    return Promise.resolve({ outcome: 'cancelled' });
  }

  return new Promise((resolve) => {
    const worker = workerFactory();
    let settled = false;

    const settle = (outcome: GenerateTimelapseVideoOutcome): void => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener('abort', onAbort);
      worker.terminate();
      resolve(outcome);
    };

    const onAbort = (): void => {
      settle({ outcome: 'cancelled' });
    };
    signal?.addEventListener('abort', onAbort, { once: true });

    worker.onmessage = (event: MessageEvent<TimelapseWorkerOutbound>): void => {
      const msg = event.data;
      switch (msg.t) {
        case 'progress':
          if (!settled) {
            onProgress?.({
              phase: msg.phase,
              framesDone: msg.framesDone,
              frameCount: msg.frameCount,
              ratio: msg.ratio,
            });
          }
          return;
        case 'result':
          settle(msg.result);
          return;
        case 'error':
          settle({ outcome: 'worker-error', message: msg.message });
          return;
      }
    };
    worker.onerror = (event: ErrorEvent): void => {
      settle({
        outcome: 'worker-error',
        message:
          typeof event.message === 'string' && event.message.length > 0
            ? event.message
            : 'timelapse worker crashed',
      });
    };

    const start: TimelapseWorkerInbound = { t: 'start', request };
    worker.postMessage(start);
  });
};
